import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, toast } from "../App";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ArrowLeft, Calendar, DollarSign, Clock, Plus, Edit, Download, Eye, Trash2, Link as LinkIcon, History, FileText, ChevronRight } from "lucide-react";
import { format } from "date-fns";
import ChangeOrderEditDialog from "@/components/ChangeOrderEditDialog";
import SubmittalStatusCard from "@/components/SubmittalStatusCard";
import ProjectAwardHistory from "@/components/projects/ProjectAwardHistory";
import { StatusPill, ProjectKpiStrip, ProjectFactsCard, ScopeCard, TeamListCard, RfiTransmittalCard } from "@/components/projects/ProjectDossier";

export default function ProjectDetail({ user }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [client, setClient] = useState(null);
  const [timesheetTotals, setTimesheetTotals] = useState({ actual: 0, earned: 0 });
  const [loading, setLoading] = useState(true);
  const [invoiceReportDialogOpen, setInvoiceReportDialogOpen] = useState(false);
  const [invoiceReportData, setInvoiceReportData] = useState(null);
  const [changeOrderDialogOpen, setChangeOrderDialogOpen] = useState(false);
  const [coExpanded, setCoExpanded] = useState(false);
  const [notesExpanded, setNotesExpanded] = useState(false);
  const [editingChangeOrder, setEditingChangeOrder] = useState(false);
  const [changeOrderData, setChangeOrderData] = useState({
    id: "",
    change_order_number: "",
    version: "1.0",
    parent_change_order_id: null,
    type: "Detailing",  // Type at change order level (Detailing or Engineering)
    line_items: [
      {
        id: crypto.randomUUID(),
        description: "",
        reference_drawing: "",
        element_name: "",  // Element Name (e.g., PLATE)
        element_quantity: "",  // Element Quantity (e.g., 33)
        action: "Addition",
        hours: "",
        cost: ""
      }
    ],
    time_impact: "",  // Auto-calculated from line items
    cost_impact: "",  // Auto-calculated but editable
    status: "pending",
    approved_hours: "",  // Shown when status is approved
    approved_cost: "",   // Shown when status is approved
    approved_date: "",   // Shown when status is approved
    branch: "",  // Branch for multi-branch projects
    hyperlink: "",
    remarks: "",
    created_at: null
  });
  const [isNewVersion, setIsNewVersion] = useState(false);
  const submitActionRef = useRef(null); // Track which button was clicked
  const [duplicateWarning, setDuplicateWarning] = useState("");
  const [viewChangeOrderDialogOpen, setViewChangeOrderDialogOpen] = useState(false);
  const [viewingChangeOrder, setViewingChangeOrder] = useState(null);
  const [deleteConfirmDialogOpen, setDeleteConfirmDialogOpen] = useState(false);
  const [deletingChangeOrderId, setDeletingChangeOrderId] = useState(null);
  const [versionHistoryDialogOpen, setVersionHistoryDialogOpen] = useState(false);
  const [versionHistoryCoNumber, setVersionHistoryCoNumber] = useState(null);

  // Check if user is a Projects department user (Team Member, Team Lead, or Manager)
  // These users have restricted access to certain features
  const isProjectsDeptUser = () => {
    return user?.department === "Projects" && 
           ["Team Member", "Team Lead", "Manager"].includes(user?.access_level);
  };

  // Who can update the work_completion_status of an approved CO?
  // Admin, any Management/PMO/HR user, OR Projects Manager / Team Lead.
  const canEditWorkStatus = () => {
    if (user?.access_level === "Admin") return true;
    if (["Management", "PMO", "HR", "Human Resources"].includes(user?.department)) return true;
    if (user?.department === "Projects" && ["Manager", "Team Lead"].includes(user?.access_level)) return true;
    return false;
  };

  // Update CO work completion status inline (without opening the edit dialog)
  const handleUpdateWorkStatus = async (changeOrderId, newStatus) => {
    try {
      await api.put(
        `/projects/${id}/change-orders/${changeOrderId}/work-completion-status`,
        { work_completion_status: newStatus }
      );
      // Optimistically reflect change in local state
      setProject((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          change_orders: (prev.change_orders || []).map((co) =>
            co.id === changeOrderId ? { ...co, work_completion_status: newStatus } : co
          ),
        };
      });
      toast.success("Work completion status updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update status");
    }
  };


  // Get only latest version of each change order number
  const getLatestChangeOrders = () => {
    if (!project || !project.change_orders) return [];
    
    const coGroups = {};
    project.change_orders.forEach(co => {
      const coNumber = co.change_order_number;
      if (!coGroups[coNumber]) {
        coGroups[coNumber] = [];
      }
      coGroups[coNumber].push(co);
    });
    
    // For each CO number, get the latest version
    const latestCos = Object.values(coGroups).map(group => {
      return group.sort((a, b) => parseFloat(b.version || "1.0") - parseFloat(a.version || "1.0"))[0];
    });
    
    return latestCos;
  };

  // Get all versions of a specific CO number
  const getChangeOrderVersions = (coNumber) => {
    if (!project || !project.change_orders) return [];
    return project.change_orders
      .filter(co => co.change_order_number === coNumber)
      .sort((a, b) => parseFloat(b.version || "1.0") - parseFloat(a.version || "1.0"));
  };

  // Helper function to check if user can view cost information
  const canViewCosts = () => {
    return user?.department === "Management" || 
           user?.department === "Sales" || 
           user?.department === "PMO" ||
           user?.department === "Accounts and Finance";
  };

  // Helper: only Management and Accounts and Finance can view Invoice Report
  const canViewInvoiceReport = () => {
    return user?.department === "Management" || 
           user?.department === "Accounts and Finance";
  };

  useEffect(() => {
    fetchProject();
    fetchTimesheetTotals();
  }, [id]);

  useEffect(() => {
    if (project && project.client_id) {
      fetchClient(project.client_id);
    }
  }, [project]);

  const fetchProject = async () => {
    try {
      const [projectRes, errorLogsRes] = await Promise.all([
        api.get(`/projects/${id}`),
        api.get(`/projects/${id}/error-logs`)
      ]);
      
      const projectData = projectRes.data;
      projectData.error_logs = errorLogsRes.data;
      
      setProject(projectData);
    } catch (error) {
      toast.error("Failed to fetch project details");
      navigate("/projects");
    } finally {
      setLoading(false);
    }
  };

  const fetchClient = async (clientId) => {
    try {
      const response = await api.get(`/clients/${clientId}`);
      setClient(response.data);
    } catch (error) {
      console.error("Failed to fetch client details");
    }
  };

  const fetchTimesheetTotals = async () => {
    try {
      const { data } = await api.get(`/projects/${id}/hours-totals`);
      setTimesheetTotals({ actual: data.actual || 0, earned: data.earned || 0 });
    } catch (error) {
      console.error("Failed to fetch timesheet totals");
    }
  };

  const checkDuplicateCoNumber = (coNumber) => {
    if (!project || !coNumber.trim()) {
      setDuplicateWarning("");
      return;
    }
    
    // Check if CO number exists (excluding current one when editing)
    const existingNumbers = project.change_orders
      .filter(co => !editingChangeOrder || co.id !== changeOrderData.id)
      .map(co => co.change_order_number.trim());
    
    if (existingNumbers.includes(coNumber.trim())) {
      // For new change orders, show error. For edits, show warning.
      if (!editingChangeOrder) {
        setDuplicateWarning(`❌ Error: "${coNumber}" already exists. You must use a different number.`);
      } else {
        setDuplicateWarning(`⚠️ Warning: "${coNumber}" already exists in this project.`);
      }
    } else {
      setDuplicateWarning("");
    }
  };

  const handleAddChangeOrder = async (e) => {
    e.preventDefault();
    
    // Validate branch selection for multi-branch projects
    const isMultiBranch = project && Array.isArray(project.branch) && project.branch.length > 1;
    if (isMultiBranch && !changeOrderData.branch) {
      toast.error("Please select a branch for this multi-branch project");
      return;
    }
    
    // Check if we're updating (editing mode AND id exists and is not empty)
    const isUpdating = editingChangeOrder && changeOrderData.id && changeOrderData.id.trim() !== "";
    
    // Check if we're creating a new version (from button click via ref)
    const isCreatingNewVersion = submitActionRef.current === 'newVersion';
    
    // For NEW change orders ONLY, check for duplicates
    // If editingChangeOrder is true, we're either updating or creating a version - skip check
    if (!editingChangeOrder) {
      const coNumber = changeOrderData.change_order_number.trim();
      const existingNumbers = project.change_orders
        .map(co => co.change_order_number.trim());
      
      if (existingNumbers.includes(coNumber)) {
        toast.error(`Change order number "${coNumber}" already exists. Please use a different number.`);
        return; // Block submission
      }
    }
    
    try {
      // Process line items to ensure proper number format
      const processedLineItems = changeOrderData.line_items.map(item => ({
        ...item,
        hours: parseFloat(item.hours) || 0,
        cost: parseFloat(item.cost) || 0
      }));

      if (isUpdating && submitActionRef.current !== 'newVersion') {
        // Update existing change order version - send payload without needing to destructure
        const updatePayload = {
          id: changeOrderData.id,
          change_order_number: changeOrderData.change_order_number,
          version: changeOrderData.version,
          parent_change_order_id: changeOrderData.parent_change_order_id,
          type: changeOrderData.type,
          line_items: processedLineItems,
          time_impact: parseFloat(changeOrderData.time_impact) || 0,
          cost_impact: parseFloat(changeOrderData.cost_impact) || 0,
          status: changeOrderData.status,
          approved_hours: parseFloat(changeOrderData.approved_hours) || 0,
          approved_cost: parseFloat(changeOrderData.approved_cost) || 0,
          approved_date: changeOrderData.status === "approved" ? (changeOrderData.approved_date || null) : null,
          branch: changeOrderData.branch || null,  // Include branch
          hyperlink: changeOrderData.hyperlink || null,
          remarks: changeOrderData.remarks || null,
          work_completion_status: changeOrderData.work_completion_status || "yet_to_start",
          created_at: changeOrderData.created_at
        };
        
        console.log("  - Calling PUT /projects/" + id + "/change-orders/" + changeOrderData.id);
        await api.put(`/projects/${id}/change-orders/${changeOrderData.id}`, updatePayload);
        toast.success("Change order updated!");
      } else if (isUpdating && submitActionRef.current === 'newVersion') {
        // Create new version - calculate next version number
        const currentVersion = parseFloat(changeOrderData.version);
        const newVersion = (Math.floor(currentVersion) + 0.1).toFixed(1);
        
        const newVersionPayload = {
          change_order_number: changeOrderData.change_order_number,
          version: newVersion,
          parent_change_order_id: changeOrderData.parent_change_order_id || changeOrderData.id,
          type: changeOrderData.type,
          line_items: processedLineItems,
          time_impact: parseFloat(changeOrderData.time_impact) || 0,
          cost_impact: parseFloat(changeOrderData.cost_impact) || 0,
          status: changeOrderData.status,
          approved_hours: parseFloat(changeOrderData.approved_hours) || 0,
          approved_cost: parseFloat(changeOrderData.approved_cost) || 0,
          approved_date: changeOrderData.status === "approved" ? (changeOrderData.approved_date || null) : null,
          branch: changeOrderData.branch || null,
          hyperlink: changeOrderData.hyperlink || null,
          remarks: changeOrderData.remarks || null
        };
        
        console.log("  - Creating new version:", newVersion);
        await api.post(`/projects/${id}/change-orders`, newVersionPayload);
        toast.success(`New version ${newVersion} created!`);
      } else {
        // Add new change order - exclude id and created_at (backend generates)
        const createPayload = {
          change_order_number: changeOrderData.change_order_number,
          version: "1.0",
          parent_change_order_id: null,
          type: changeOrderData.type,
          line_items: processedLineItems,
          time_impact: parseFloat(changeOrderData.time_impact) || 0,
          cost_impact: parseFloat(changeOrderData.cost_impact) || 0,
          status: changeOrderData.status,
          approved_hours: parseFloat(changeOrderData.approved_hours) || 0,
          approved_cost: parseFloat(changeOrderData.approved_cost) || 0,
          approved_date: changeOrderData.status === "approved" ? (changeOrderData.approved_date || null) : null,
          branch: changeOrderData.branch || null,  // Include branch
          hyperlink: changeOrderData.hyperlink || null,
          remarks: changeOrderData.remarks || null
        };
        
        console.log("  - Calling POST /projects/" + id + "/change-orders");
        await api.post(`/projects/${id}/change-orders`, createPayload);
        toast.success("Change order added!");
      }
      
      setChangeOrderDialogOpen(false);
      setChangeOrderData({
        id: "",
        change_order_number: "",
        version: "1.0",
        parent_change_order_id: null,
        type: "Detailing",
        line_items: [{
          id: crypto.randomUUID(),
          description: "",
          reference_drawing: "",
          element_name: "",
          element_quantity: "",
          action: "Addition",
          hours: "",
          cost: ""
        }],
        time_impact: "",
        cost_impact: "",
        status: "pending",
        approved_hours: "",
        approved_cost: "",
        approved_date: "",
        branch: "",  // Reset branch
        hyperlink: "",
        remarks: "",
        created_at: null
      });
      setDuplicateWarning("");
      setEditingChangeOrder(false);
      setIsNewVersion(false);
      submitActionRef.current = null; // Reset action ref
      fetchProject();
    } catch (error) {
      console.error("❌ Change Order Save Error:", error);
      toast.error(error.response?.data?.detail || "Failed to save change order");
    }
  };

  const handleEditChangeOrder = (co) => {
    console.log("📝 Edit Change Order Clicked:");
    console.log("  - Change Order ID:", co.id);
    console.log("  - Full CO Object:", co);
    
    // Ensure line_items exists and has at least one item
    // Handle backward compatibility for old format
    let lineItems = [];
    if (co.line_items && co.line_items.length > 0) {
      // New format with line_items - just use as is
      lineItems = co.line_items.map(item => ({
        ...item,
        element_name: item.element_name || "",
        element_quantity: item.element_quantity || ""
      }));
    } else {
      // Old format without line_items - convert
      lineItems = [{
        id: crypto.randomUUID(),
        description: co.description || "",
        reference_drawing: "",
        element_name: "",
        element_quantity: "",
        action: "Addition",
        hours: co.time_impact ? co.time_impact.toString() : "",
        cost: co.cost_impact ? co.cost_impact.toString() : ""
      }];
    }
    
    setChangeOrderData({
      id: co.id,
      change_order_number: co.change_order_number,
      version: co.version || "1.0",
      parent_change_order_id: co.parent_change_order_id || null,
      type: co.type || "Detailing",
      line_items: lineItems,
      time_impact: co.time_impact.toString(),
      cost_impact: co.cost_impact.toString(),
      status: co.status,
      approved_hours: (co.approved_hours || 0).toString(),
      approved_cost: (co.approved_cost || 0).toString(),
      approved_date: co.approved_date || "",
      branch: co.branch || "",  // Include branch
      hyperlink: co.hyperlink || "",
      remarks: co.remarks || "",
      created_at: co.created_at
    });
    setEditingChangeOrder(true);
    setIsNewVersion(false);
    setChangeOrderDialogOpen(true);
    
    console.log("  - editingChangeOrder set to: true");
    console.log("  - Dialog opened with ID:", co.id);
  };

  const handleAddNewChangeOrder = () => {
    console.log("➕ Add New Change Order Clicked - Resetting state");
    
    // Reset to clean state for adding new change order
    setChangeOrderData({
      id: "",
      change_order_number: "",
      version: "1.0",
      parent_change_order_id: null,
      type: "Detailing",
      line_items: [
        {
          id: crypto.randomUUID(),
          description: "",
          reference_drawing: "",
          element_name: "",
          element_quantity: "",
          action: "Addition",
          hours: "",
          cost: ""
        }
      ],
      time_impact: "",
      cost_impact: "",
      status: "pending",
      approved_hours: "",
      approved_cost: "",
      approved_date: "",
      branch: "",  // Reset branch
      hyperlink: "",
      remarks: "",
      created_at: null
    });
    setEditingChangeOrder(false);
    setIsNewVersion(false);
    setDuplicateWarning("");
    setChangeOrderDialogOpen(true);
    
    console.log("  - editingChangeOrder set to: false");
    console.log("  - Fresh state ready for new CO");
  };

  // Line Item Management Functions
  const addLineItem = () => {
    const newLineItem = {
      id: crypto.randomUUID(),
      description: "",
      reference_drawing: "",
      element_name: "",
      element_quantity: "",
      action: "Addition",
      hours: "",
      cost: ""
    };
    setChangeOrderData({
      ...changeOrderData,
      line_items: [...changeOrderData.line_items, newLineItem]
    });
  };

  const removeLineItem = (lineItemId) => {
    // Prevent removing the last line item
    if (changeOrderData.line_items.length <= 1) {
      toast.error("At least one line item must remain");
      return;
    }
    setChangeOrderData({
      ...changeOrderData,
      line_items: changeOrderData.line_items.filter(item => item.id !== lineItemId)
    });
  };

  const updateLineItem = (lineItemId, field, value) => {
    const updatedLineItems = changeOrderData.line_items.map(item => {
      if (item.id === lineItemId) {
        return { ...item, [field]: value };
      }
      return item;
    });
    setChangeOrderData({
      ...changeOrderData,
      line_items: updatedLineItems
    });
    // Recalculate totals
    calculateTotals(updatedLineItems);
  };

  const calculateTotals = (lineItems) => {
    const totalHours = lineItems.reduce((sum, item) => {
      return sum + (parseFloat(item.hours) || 0);
    }, 0);
    const totalCost = lineItems.reduce((sum, item) => {
      return sum + (parseFloat(item.cost) || 0);
    }, 0);
    
    setChangeOrderData(prev => ({
      ...prev,
      time_impact: totalHours.toString(),
      cost_impact: totalCost.toString()
    }));
  };


  const handleViewChangeOrder = (co) => {
    console.log("👁️ View Change Order Clicked:", co.id);
    setViewingChangeOrder(co);
    setViewChangeOrderDialogOpen(true);
  };

  const handleDownloadChangeOrder = async (co, format = 'excel') => {
    try {
      console.log(`📥 Downloading change order as ${format}:`, co.id);
      
      // Call backend to generate Excel or PDF
      const endpoint = format === 'pdf' 
        ? `/projects/${id}/change-orders/${co.id}/download-pdf`
        : `/projects/${id}/change-orders/${co.id}/download`;
      
      const response = await api.get(endpoint, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Generate filename
      const projectName = project?.name?.replace(/[^a-z0-9]/gi, '_') || 'PROJECT';
      const coNumber = co.change_order_number.replace(/[^a-z0-9]/gi, '_');
      const extension = format === 'pdf' ? 'pdf' : 'xlsx';
      
      // Excel includes client name, PDF does not
      const filename = format === 'pdf'
        ? `${projectName}_Change_Order_${coNumber}.${extension}`
        : `${client?.client_name?.replace(/[^a-z0-9]/gi, '_') || 'CLIENT'}_${projectName}_Change_Order_${coNumber}.${extension}`;
      
      link.setAttribute('download', filename);
      
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Change order ${format.toUpperCase()} downloaded successfully!`);
    } catch (error) {
      console.error("Download error:", error);
      toast.error(`Failed to download change order ${format.toUpperCase()}`);
    }
  };

  const handleDeleteChangeOrderClick = (co) => {
    console.log("🗑️ Delete Change Order Clicked:", co.id);
    setDeletingChangeOrderId(co.id);
    setDeleteConfirmDialogOpen(true);
  };

  const handleDeleteChangeOrder = async () => {
    if (!deletingChangeOrderId) return;
    
    try {
      console.log("🗑️ Deleting change order:", deletingChangeOrderId);
      await api.delete(`/projects/${id}/change-orders/${deletingChangeOrderId}`);
      toast.success("Change order deleted successfully!");
      setDeleteConfirmDialogOpen(false);
      setDeletingChangeOrderId(null);
      fetchProject();
    } catch (error) {
      console.error("❌ Delete Change Order Error:", error);
      toast.error(error.response?.data?.detail || "Failed to delete change order");
    }
  };


  const getTotalProjectHours = () => {
    if (!project) return 0;
    const approvedHours = (project.change_orders || [])
      .filter(co => co.status === "approved")
      .reduce((sum, co) => sum + (co.approved_hours || co.time_impact || 0), 0);
    return project.estimated_hours + approvedHours;
  };

  const getTotalProjectCost = () => {
    if (!project) return 0;
    const approvedCost = (project.change_orders || [])
      .filter(co => co.status === "approved")
      .reduce((sum, co) => sum + (co.approved_cost || co.cost_impact || 0), 0);
    return project.base_price + approvedCost;
  };

  // Helper to get USD equivalent for CAD projects
  const getUsdEquivalent = (cadValue, usdValue) => {
    if (project?.currency !== "CAD") return null;
    // Use stored USD value if available, otherwise calculate with default rate
    if (usdValue !== undefined && usdValue !== null) {
      return usdValue;
    }
    const rate = project?.usd_conversion_rate || 0.74;
    return cadValue * rate;
  };

  // Format amount with currency label
  const formatWithCurrency = (amount, showUsd = true) => {
    if (!project) return "$0";
    const currency = project.currency || "USD";
    const formattedAmount = amount.toLocaleString();
    
    if (currency === "CAD" && showUsd) {
      const usdAmount = getUsdEquivalent(amount);
      return (
        <div>
          <span>{formattedAmount} CAD</span>
          {usdAmount !== null && (
            <div className="text-xs text-gray-500">≈ {usdAmount.toLocaleString(undefined, {maximumFractionDigits: 0})} USD</div>
          )}
        </div>
      );
    }
    return `${formattedAmount} ${currency}`;
  };

  // Get total approved change orders cost with USD equivalent
  const getApprovedChangeOrdersCost = () => {
    if (!project) return { cad: 0, usd: 0 };
    const approvedCOs = (project.change_orders || []).filter(co => co.status === "approved");
    const cadTotal = approvedCOs.reduce((sum, co) => sum + (co.cost_impact || 0), 0);
    const usdTotal = approvedCOs.reduce((sum, co) => sum + (co.usd_cost_impact || co.cost_impact * (project.usd_conversion_rate || 0.74)), 0);
    return { cad: cadTotal, usd: usdTotal };
  };

  // Get total project value with USD equivalent
  const getTotalProjectValueWithUsd = () => {
    if (!project) return { cad: 0, usd: 0 };
    const approvedCost = getApprovedChangeOrdersCost();
    const baseUsd = project.usd_base_price || (project.base_price * (project.usd_conversion_rate || 0.74));
    return {
      cad: project.base_price + approvedCost.cad,
      usd: baseUsd + approvedCost.usd
    };
  };

  const handleViewInvoiceReport = async () => {
    try {
      const response = await api.get(`/projects/${id}/invoice-report-data`);
      setInvoiceReportData(response.data);
      setInvoiceReportDialogOpen(true);
    } catch (error) {
      console.error("Invoice report fetch error:", error);
      toast.error("Failed to load invoice report");
    }
  };

  const handleDownloadInvoiceReport = async () => {
    try {
      const response = await api.get(`/projects/${id}/invoice-report`, {
        responseType: 'blob'
      });
      
      if (!(response.data instanceof Blob)) {
        throw new Error("Invalid response type");
      }
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const projectNumber = project.project_number || "PROJECT";
      link.setAttribute('download', `Invoice_Report_${projectNumber}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Invoice report downloaded successfully!");
    } catch (error) {
      console.error("Invoice report download error:", error);
      toast.error("Failed to download invoice report");
    }
  };

  const approvedCoHours = (project?.change_orders || []).filter((co) => co.status === "approved").reduce((sum, co) => sum + (co.approved_hours ?? co.time_impact ?? 0), 0);

  if (loading) {
    return (
      <Layout user={user}>
        <div className="animate-pulse space-y-6">
          <div className="h-12 bg-gray-200 rounded w-1/3" />
          <div className="h-64 bg-gray-200 rounded" />
        </div>
      </Layout>
    );
  }

  if (!project) {
    return (
      <Layout user={user}>
        <div className="text-center py-12">
          <p className="text-md3-on-surface-variant">Project not found or you don't have access to view it.</p>
          <Button variant="outline" className="mt-4" onClick={() => navigate("/projects")}>
            <ArrowLeft className="mr-2 h-4 w-4" /> Back to Projects
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user}>
      <div className="space-y-6" data-testid="project-detail-page">
        <div>
          <Button variant="ghost" className="hover:bg-md3-surface-container-high rounded-md3-full transition-all duration-200 mb-3 -ml-3" onClick={() => navigate("/projects")}>
            <ArrowLeft className="mr-2 h-4 w-4" /> Back to Projects
          </Button>
          <div className="flex flex-wrap justify-between items-start gap-4">
            <div className="min-w-0">
              <div className="flex items-center gap-3 mb-1">
                {project.project_number && <span className="font-mono text-lg font-bold tracking-wide text-white bg-md3-primary px-3 py-0.5 rounded-md shadow-sm" data-testid="project-number">{project.project_number}</span>}
                <StatusPill status={project.status} />
              </div>
              <h1 className="font-[Inter,sans-serif] text-2xl lg:text-[28px] font-bold text-gray-900 leading-tight tracking-tight" data-testid="project-title">{project.name}</h1>
              <p className="text-sm text-gray-600 mt-1" data-testid="project-subtitle">
                {client ? `${client.client_name} (${client.client_number})` : (project.client_name || "Unknown client")}
              </p>
            </div>
            <div className="flex gap-2">
              {canViewInvoiceReport() && (
                <Button variant="outline" className="rounded-md3-full" onClick={handleViewInvoiceReport} data-testid="view-invoice-report-button">
                  <Eye className="mr-2 h-4 w-4" /> Invoice Report
                </Button>
              )}
              <Button className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 rounded-md3-full" onClick={() => navigate(`/projects/${project.id}/planning`)} data-testid="view-planning-button">
                <Calendar className="mr-2 h-4 w-4" /> Project Planning
              </Button>
            </div>
          </div>
        </div>

        <ProjectKpiStrip
          project={project}
          showCosts={canViewCosts()}
          showEstimated={!isProjectsDeptUser()}
          totalValue={getTotalProjectValueWithUsd().cad}
          coValue={getApprovedChangeOrdersCost().cad}
          allocated={project.allocated_hours || 0}
          coHours={approvedCoHours}
          timesheet={timesheetTotals}
        />

        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_340px] gap-6 items-start">
          <div className="space-y-6 min-w-0">
            {project.client_notes_project_start && project.client_notes_project_start.trim() && (
              <Card className="border border-amber-200 bg-amber-50/60 shadow-sm rounded-xl" data-testid="client-notes-card">
                <CardHeader className={notesExpanded ? "pb-2" : ""}>
                  <CardTitle className="text-base font-semibold">
                    <button type="button" onClick={() => setNotesExpanded((e) => !e)} className="flex items-center gap-2 hover:text-amber-800 transition-colors" data-testid="client-notes-toggle">
                      <ChevronRight className={`h-4 w-4 transition-transform duration-200 ${notesExpanded ? "rotate-90" : ""}`} />
                      <FileText className="h-4 w-4 text-amber-700" /> Notes from Client (Project Start)
                    </button>
                  </CardTitle>
                </CardHeader>
                {notesExpanded && (
                  <CardContent className="pt-0">
                    <p className="text-sm text-gray-800 whitespace-pre-wrap" data-testid="client-notes-text">{project.client_notes_project_start}</p>
                  </CardContent>
                )}
              </Card>
            )}

            {(user?.department === "Management" || user?.access_level === "Admin") && (
              <ProjectAwardHistory project={project} onProjectUpdated={fetchProject} />
            )}

            <SubmittalStatusCard projectId={id} project={project} />

            <RfiTransmittalCard projectId={id} />

          {/* Change Orders Section */}
          <Card className="border border-gray-100 shadow-sm rounded-xl bg-white" data-testid="project-change-orders-card">
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle className="text-base font-semibold text-md3-on-surface">
                  <button type="button" onClick={() => setCoExpanded((e) => !e)} className="flex items-center gap-2 hover:text-md3-primary transition-colors" data-testid="change-orders-toggle">
                    <ChevronRight className={`h-4 w-4 transition-transform duration-200 ${coExpanded ? "rotate-90" : ""}`} />
                    Change Orders
                    <span className="text-xs font-normal text-md3-on-surface-variant" data-testid="change-orders-count">({project?.change_orders?.length || 0})</span>
                  </button>
                </CardTitle>
                {/* Add Change Order - hidden for Projects dept users */}
                {!isProjectsDeptUser() && (
                  <Button size="sm" className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full" onClick={handleAddNewChangeOrder}>
                    <Plus className="mr-2 h-4 w-4" /> Add Change Order
                  </Button>
                )}
              </div>
            </CardHeader>
            
            {/* Shared Change Order Edit Dialog */}
            <ChangeOrderEditDialog
              open={changeOrderDialogOpen}
              onOpenChange={setChangeOrderDialogOpen}
              changeOrder={editingChangeOrder ? changeOrderData : null}
              project={project}
              onSuccess={() => {
                setChangeOrderDialogOpen(false);
                fetchProject(); // Refresh project data
                setEditingChangeOrder(false);
                setChangeOrderData({
                  id: "",
                  change_order_number: "",
                  version: "1.0",
                  parent_change_order_id: null,
                  type: "Detailing",
                  line_items: [
                    {
                      id: crypto.randomUUID(),
                      description: "",
                      reference_drawing: "",
                      element_name: "",
                      element_quantity: "",
                      action: "Addition",
                      hours: "",
                      cost: ""
                    }
                  ],
                  time_impact: 0,
                  cost_impact: 0,
                  status: "pending",
                  approved_hours: 0,
                  approved_cost: 0,
                  approved_date: "",
                  branch: "",
                  hyperlink: "",
                  remarks: "",
                  created_at: null
                });
              }}
              editMode={editingChangeOrder}
              canViewCosts={canViewCosts}
            />

            {coExpanded && <CardContent className="pt-6">
              {/* Change Orders List */}
              {project && project.change_orders && project.change_orders.length > 0 ? (
                <div className="space-y-6">
                  {/* Group change orders by number and show latest version of each */}
                  {Object.entries(
                    project.change_orders.reduce((groups, co) => {
                      const coNumber = co.change_order_number;
                      if (!groups[coNumber]) {
                        groups[coNumber] = [];
                      }
                      groups[coNumber].push(co);
                      return groups;
                    }, {})
                  ).map(([coNumber, versions]) => {
                    // Sort versions by version number and get the latest
                    const sortedVersions = versions.sort((a, b) => {
                      const versionA = parseFloat(a.version || "1.0");
                      const versionB = parseFloat(b.version || "1.0");
                      return versionB - versionA;
                    });
                    const latestVersion = sortedVersions[0];
                    const hasMultipleVersions = versions.length > 1;

                    return (
                      <div key={coNumber} className="border rounded-md3-lg p-4 bg-md3-surface-container hover:bg-md3-surface transition-colors">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="font-mono font-semibold text-blue-600">{coNumber}</span>
                              <Badge variant="outline" className="text-xs font-mono">
                                v{latestVersion.version || "1.0"}
                              </Badge>
                              <Badge 
                                variant={
                                  latestVersion.status === "approved" ? "default" : 
                                  latestVersion.status === "rejected" ? "destructive" : "secondary"
                                }
                                className={
                                  latestVersion.status === "approved" ? "bg-green-100 text-green-800" :
                                  latestVersion.status === "rejected" ? "bg-red-100 text-red-800" : ""
                                }
                              >
                                {latestVersion.status}
                              </Badge>
                              {hasMultipleVersions && (
                                <Badge variant="outline" className="text-xs">
                                  {versions.length} versions
                                </Badge>
                              )}
                            </div>
                            <p className="text-md3-body-small text-md3-on-surface-variant mb-2">
                              {latestVersion.line_items && latestVersion.line_items.length > 0 
                                ? latestVersion.line_items[0].description 
                                : "No description"}
                            </p>
                            <div className="flex items-center gap-4 text-xs text-md3-on-surface-variant">
                              {/* Hours display - Projects dept only sees approved hours */}
                              {!isProjectsDeptUser() ? (
                                <span className="flex items-center">
                                  <Clock className="h-3 w-3 mr-1" />
                                  {latestVersion.status === "approved" 
                                    ? (latestVersion.approved_hours || latestVersion.time_impact || 0)
                                    : (latestVersion.time_impact || 0)}h
                                  {latestVersion.status === "approved" && latestVersion.approved_hours !== latestVersion.time_impact && (
                                    <span className="ml-1 text-gray-400">(req: {latestVersion.time_impact}h)</span>
                                  )}
                                </span>
                              ) : (
                                latestVersion.status === "approved" && (
                                  <span className="flex items-center">
                                    <Clock className="h-3 w-3 mr-1" />
                                    {latestVersion.approved_hours || latestVersion.time_impact || 0}h
                                  </span>
                                )
                              )}
                              {canViewCosts() && (
                                <span className="flex items-center">
                                  <DollarSign className="h-3 w-3 mr-1" />
                                  {latestVersion.status === "approved"
                                    ? (latestVersion.approved_cost || latestVersion.cost_impact || 0).toLocaleString()
                                    : (latestVersion.cost_impact || 0).toLocaleString()} {project.currency || "USD"}
                                  {project.currency === "CAD" && latestVersion.usd_cost_impact && (
                                    <span className="ml-1 text-gray-400">(≈{latestVersion.usd_cost_impact.toLocaleString(undefined, {maximumFractionDigits: 0})} USD)</span>
                                  )}
                                </span>
                              )}
                              {/* Items count - hidden for Projects dept users */}
                              {!isProjectsDeptUser() && (
                                <span className="flex items-center">
                                  📄 {latestVersion.line_items ? latestVersion.line_items.length : 0} items
                                </span>
                              )}
                              {latestVersion.created_at && (
                                <span className="flex items-center text-md3-on-surface-variant">
                                  {format(new Date(latestVersion.created_at), "MMM d, yyyy")}
                                </span>
                              )}
                            </div>
                            {/* Work Completion Status — editable inline for Approved COs by Projects Manager/Team Lead */}
                            {latestVersion.status === "approved" && (
                              <div className="mt-3 flex items-center gap-2" data-testid={`co-work-status-row-${coNumber}`}>
                                <span className="text-xs text-md3-on-surface-variant">Work Status:</span>
                                {canEditWorkStatus() ? (
                                  <Select
                                    value={latestVersion.work_completion_status || "yet_to_start"}
                                    onValueChange={(value) => handleUpdateWorkStatus(latestVersion.id, value)}
                                  >
                                    <SelectTrigger
                                      className="h-7 w-44 text-xs bg-white"
                                      data-testid={`co-work-status-select-${coNumber}`}
                                    >
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="yet_to_start">Yet to Start</SelectItem>
                                      <SelectItem value="in_progress">In Progress</SelectItem>
                                      <SelectItem value="waiting_for_rfi">Waiting for RFI</SelectItem>
                                      <SelectItem value="released_for_approval">Released for Approval</SelectItem>
                                      <SelectItem value="released_for_fab">Released for Fab</SelectItem>
                                    </SelectContent>
                                  </Select>
                                ) : (
                                  <Badge
                                    variant="outline"
                                    className={
                                      latestVersion.work_completion_status === "released_for_fab" ? "bg-green-100 text-green-800 border-green-200" :
                                      latestVersion.work_completion_status === "released_for_approval" ? "bg-purple-100 text-purple-800 border-purple-200" :
                                      latestVersion.work_completion_status === "in_progress" ? "bg-blue-100 text-blue-800 border-blue-200" :
                                      latestVersion.work_completion_status === "waiting_for_rfi" ? "bg-amber-100 text-amber-800 border-amber-200" :
                                      "bg-gray-100 text-gray-700 border-gray-200"
                                    }
                                  >
                                    {{
                                      yet_to_start: "Yet to Start",
                                      in_progress: "In Progress",
                                      waiting_for_rfi: "Waiting for RFI",
                                      released_for_approval: "Released for Approval",
                                      released_for_fab: "Released for Fab",
                                    }[latestVersion.work_completion_status || "yet_to_start"] || "Yet to Start"}
                                  </Badge>
                                )}
                              </div>
                            )}
                          </div>
                          <div className="flex gap-2">
                            <Button variant="ghost" className="hover:bg-md3-surface-container-high rounded-md3-full transition-all duration-200"
                              size="sm"
                              onClick={() => handleViewChangeOrder(latestVersion)}
                              title="View Change Order"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            {/* Edit Change Order - hidden for Projects dept users */}
                            {!isProjectsDeptUser() && (
                              <Button variant="ghost" className="hover:bg-md3-surface-container-high rounded-md3-full transition-all duration-200"
                                size="sm"
                                onClick={() => handleEditChangeOrder(latestVersion)}
                                title="Edit Change Order"
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                            )}
                            <Button variant="ghost" className="hover:bg-md3-surface-container-high rounded-md3-full transition-all duration-200"
                              size="sm"
                              onClick={() => {
                                setVersionHistoryCoNumber(coNumber);
                                setVersionHistoryDialogOpen(true);
                              }}
                              title="View Version History"
                            >
                              <History className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-md3-on-surface-variant">
                  <FileText className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                  <h3 className="text-lg font-medium text-md3-on-surface mb-1">No Change Orders</h3>
                  <p className="text-md3-body-small">Get started by adding your first change order to track project modifications.</p>
                </div>
              )}
            </CardContent>}
          </Card>
          </div>

          <aside className="space-y-6 lg:sticky lg:top-4" data-testid="project-sidebar">
            <ProjectFactsCard project={project} showCosts={canViewCosts()} coValue={getApprovedChangeOrdersCost().cad} coHours={approvedCoHours} />
            <ScopeCard scope={project.scope_of_work} />
            <TeamListCard projectId={id} />
          </aside>
        </div>

        {/* View Change Order Dialog - Excel Format */}
        <Dialog open={viewChangeOrderDialogOpen} onOpenChange={setViewChangeOrderDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-white">
            <DialogHeader>
              <DialogTitle className="text-md3-headline-small text-md3-on-surface font-semibold ">Change Order - {viewingChangeOrder?.change_order_number}</DialogTitle>
              <DialogDescription className="text-md3-body-medium text-gray-600">Change order details in Excel format</DialogDescription>
            </DialogHeader>
            {viewingChangeOrder && (
              <div className="space-y-6">
                {/* Header Information */}
                <div className="bg-blue-50 p-4 rounded-md3-lg border border-blue-200 space-y-3">
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label className="text-xs font-semibold text-blue-900 text-md3-label-large text-gray-700 font-medium">CLIENT NAME</Label>
                      <div className="text-md3-body-small font-medium">{client?.client_name || 'N/A'}</div>
                    </div>
                    <div>
                      <Label className="text-xs font-semibold text-blue-900 text-md3-label-large text-gray-700 font-medium">PROJECT NAME</Label>
                      <div className="text-md3-body-small font-medium">{project?.name || 'N/A'}</div>
                    </div>
                    <div>
                      <Label className="text-xs font-semibold text-blue-900 text-md3-label-large text-gray-700 font-medium">PROJECT NO</Label>
                      <div className="text-md3-body-small font-medium">{project?.project_number || 'N/A'}</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label className="text-xs font-semibold text-blue-900 text-md3-label-large text-gray-700 font-medium">DATE</Label>
                      <div className="text-md3-body-small font-medium">
                        {viewingChangeOrder.created_at && format(new Date(viewingChangeOrder.created_at), "MM-dd-yyyy")}
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs font-semibold text-blue-900 text-md3-label-large text-gray-700 font-medium">CHANGE ORDER NO</Label>
                      <div className="text-md3-body-small font-medium flex items-center gap-2">
                        {viewingChangeOrder.change_order_number}
                        <Badge variant="outline" className="text-xs font-mono">
                          v{viewingChangeOrder.version || "1.0"}
                        </Badge>
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs font-semibold text-blue-900 text-md3-label-large text-gray-700 font-medium">TYPE</Label>
                      <Badge variant="outline" className="font-semibold">
                        {viewingChangeOrder.type || 'Detailing'}
                      </Badge>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs font-semibold text-blue-900 text-md3-label-large text-gray-700 font-medium">STATUS</Label>
                      <div className="mt-1">
                        <Badge variant={viewingChangeOrder.status === "approved" ? "success" : viewingChangeOrder.status === "rejected" ? "destructive" : "secondary"}>
                          {viewingChangeOrder.status.toUpperCase()}
                        </Badge>
                      </div>
                    </div>
                    {viewingChangeOrder.remarks && (
                      <div>
                        <Label className="text-xs font-semibold text-blue-900 text-md3-label-large text-gray-700 font-medium">REMARKS</Label>
                        <div className="text-md3-body-small mt-1">{viewingChangeOrder.remarks}</div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Line Items Table */}
                <div>
                  <Label className="text-md3-body-medium font-semibold mb-3 block ">Line Items</Label>
                  <div className="border rounded-md3-lg overflow-hidden">
                    <Table className="bg-md3-surface-container rounded-md3-md">
                      <TableHeader>
                        <TableRow className="bg-md3-surface-container">
                          <TableHead className="w-[60px] text-center font-semibold">SI.NO</TableHead>
                          <TableHead className="font-semibold">DESCRIPTION OF CHANGES</TableHead>
                          <TableHead className="w-[120px] font-semibold">REF. DRAWING</TableHead>
                          <TableHead className="w-[120px] font-semibold">ELEMENTS</TableHead>
                          <TableHead className="w-[100px] font-semibold">ACTION</TableHead>
                          {!isProjectsDeptUser() && <TableHead className="w-[80px] text-right font-semibold">HOURS</TableHead>}
                          {canViewCosts() && <TableHead className="w-[100px] text-right font-semibold">COST ($)</TableHead>}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {viewingChangeOrder.line_items && viewingChangeOrder.line_items.length > 0 ? (
                          (() => {
                            // Sort line items: Addition first, then Modification, then Deletion
                            const actionOrder = { 'Addition': 1, 'Modification': 2, 'Deletion': 3 };
                            const sortedItems = [...viewingChangeOrder.line_items].sort((a, b) => {
                              return (actionOrder[a.action] || 999) - (actionOrder[b.action] || 999);
                            });
                            
                            return sortedItems.map((item, index) => (
                              <TableRow key={item.id || index}>
                                <TableCell className="text-center font-medium">{index + 1}</TableCell>
                                <TableCell className="text-md3-body-small whitespace-pre-wrap">{item.description}</TableCell>
                                <TableCell className="text-md3-body-small">{item.reference_drawing || '-'}</TableCell>
                                <TableCell className="text-md3-body-small">
                                  {item.element_name && item.element_quantity 
                                    ? `${item.element_name} (${item.element_quantity})`
                                    : item.element_name || item.element_quantity || '-'}
                                </TableCell>
                                <TableCell className="text-md3-body-small">{item.action}</TableCell>
                                {!isProjectsDeptUser() && <TableCell className="text-right font-medium">{item.hours}</TableCell>}
                                {canViewCosts() && <TableCell className="text-right font-medium">${item.cost.toLocaleString()}</TableCell>}
                              </TableRow>
                            ));
                          })()
                        ) : (
                          <TableRow>
                            <TableCell colSpan={canViewCosts() ? (isProjectsDeptUser() ? 6 : 7) : (isProjectsDeptUser() ? 5 : 6)} className="text-center text-md3-on-surface-variant">
                              No line items
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </div>

                {/* Totals Section */}
                <div className="bg-md3-surface p-4 rounded-md3-lg border space-y-2">
                  {(!isProjectsDeptUser() || viewingChangeOrder.status === "approved") && (
                    <div className="flex justify-between items-center">
                      <span className="font-semibold">
                        {viewingChangeOrder.status === "approved" ? "Approved Hours:" : "Requested Hours:"}
                      </span>
                      <span className="text-md3-title-large text-blue-600">
                        {viewingChangeOrder.status === "approved" 
                          ? (viewingChangeOrder.approved_hours || viewingChangeOrder.time_impact)
                          : viewingChangeOrder.time_impact} hours
                      </span>
                    </div>
                  )}
                  {!isProjectsDeptUser() && viewingChangeOrder.status === "approved" && viewingChangeOrder.approved_hours !== viewingChangeOrder.time_impact && (
                    <div className="flex justify-between items-center text-sm text-gray-500">
                      <span>Originally Requested:</span>
                      <span>{viewingChangeOrder.time_impact} hours</span>
                    </div>
                  )}
                  {canViewCosts() && (
                    <div className="flex justify-between items-center pt-2 border-t">
                      <span className="font-semibold">
                        {viewingChangeOrder.status === "approved" ? "Approved Cost:" : "Requested Cost:"}
                      </span>
                      <div className="text-right">
                        <span className="text-md3-title-large text-green-600">
                          {viewingChangeOrder.status === "approved"
                            ? (viewingChangeOrder.approved_cost || viewingChangeOrder.cost_impact || 0).toLocaleString()
                            : (viewingChangeOrder.cost_impact || 0).toLocaleString()} {project?.currency || "USD"}
                        </span>
                        {project?.currency === "CAD" && viewingChangeOrder.usd_cost_impact && (
                          <div className="text-sm text-gray-500">≈ {viewingChangeOrder.usd_cost_impact.toLocaleString(undefined, {maximumFractionDigits: 0})} USD</div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Additional Info */}
                {viewingChangeOrder.hyperlink && (
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold text-md3-label-large text-gray-700 font-medium">Document Link</Label>
                    <div className="px-3 py-2 bg-md3-surface border rounded-md3-md">
                      <a 
                        href={viewingChangeOrder.hyperlink} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline text-md3-body-small flex items-center"
                      >
                        <LinkIcon className="h-3 w-3 mr-1" />
                        {viewingChangeOrder.hyperlink}
                      </a>
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex justify-between gap-2 pt-4 border-t">
                  <div className="flex gap-2">
                    {!isProjectsDeptUser() && (
                      <>
                        <Button variant="outline" className="transition-all duration-200 rounded-md3-full bg-green-50 hover:bg-green-100 border-green-300"
                          onClick={() => handleDownloadChangeOrder(viewingChangeOrder, 'excel')}
                        >
                          <Download className="mr-2 h-4 w-4" />
                          Download Excel
                        </Button>
                        <Button variant="outline" className="transition-all duration-200 rounded-md3-full bg-red-50 hover:bg-red-100 border-red-300"
                          onClick={() => handleDownloadChangeOrder(viewingChangeOrder, 'pdf')}
                        >
                          <Download className="mr-2 h-4 w-4" />
                          Download PDF
                        </Button>
                      </>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" className="border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full" onClick={() => setViewChangeOrderDialogOpen(false)}>
                      Close
                    </Button>
                    {/* Edit button in view dialog - hidden for Projects dept users */}
                    {!isProjectsDeptUser() && (
                      <Button className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full" onClick={() => {
                        setViewChangeOrderDialogOpen(false);
                        handleEditChangeOrder(viewingChangeOrder);
                      }}>
                        <Edit className="mr-2 h-4 w-4" />
                        Edit
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>


        {/* Version History Dialog */}
        <Dialog open={versionHistoryDialogOpen} onOpenChange={setVersionHistoryDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto bg-white">
            <DialogHeader>
              <DialogTitle className="text-md3-headline-small text-md3-on-surface font-semibold ">Version History - {versionHistoryCoNumber}</DialogTitle>
              <DialogDescription className="text-md3-body-medium text-gray-600">All versions of this change order</DialogDescription>
            </DialogHeader>
            {versionHistoryCoNumber && (
              <div className="space-y-3">
                {getChangeOrderVersions(versionHistoryCoNumber).map((co, index) => {
                  const bgColors = ['bg-blue-50/50 border-blue-200', 'bg-green-50/50 border-green-200', 'bg-purple-50/50 border-purple-200', 'bg-amber-50/50 border-amber-200', 'bg-pink-50/50 border-pink-200', 'bg-cyan-50/50 border-cyan-200'];
                  return (
                  <div 
                    key={co.id} 
                    className={`p-4 border rounded-md3-lg ${bgColors[index % bgColors.length]}`}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge variant="outline" className="font-mono font-semibold">
                            v{co.version}
                          </Badge>
                          {index === 0 && (
                            <Badge variant="default" className="bg-blue-600">
                              Latest
                            </Badge>
                          )}
                          <Badge variant={co.status === "approved" ? "success" : co.status === "rejected" ? "destructive" : "secondary"}>
                            {co.status}
                          </Badge>
                        </div>
                        <p className="text-md3-body-small text-md3-on-surface-variant mb-2 line-clamp-2">
                          {co.line_items && co.line_items.length > 0 
                            ? co.line_items[0].description 
                            : co.description || "No description"}
                        </p>
                        {co.remarks && (
                          <p className="text-xs text-md3-on-surface-variant italic">
                            <strong>Remarks:</strong> {co.remarks}
                          </p>
                        )}
                      </div>
                      <div className="flex gap-1">
                        <Button variant="ghost"
                          size="sm"
                          onClick={() => {
                            setVersionHistoryDialogOpen(false);
                            handleViewChangeOrder(co);
                          }}
                          className="h-8 w-8 p-0 hover:bg-md3-surface-container-high rounded-md3-full transition-all duration-200"
                          title="View Details"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        {/* Edit button in version history - hidden for Projects dept users */}
                        {index === 0 && !isProjectsDeptUser() && (
                          <Button variant="ghost"
                            size="sm"
                            onClick={() => {
                              setVersionHistoryDialogOpen(false);
                              handleEditChangeOrder(co);
                            }}
                            className="h-8 w-8 p-0 hover:bg-md3-surface-container-high rounded-md3-full transition-all duration-200"
                            title="Edit Latest Version"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-md3-on-surface-variant mt-2 pt-2 border-t">
                      <span className="flex items-center">
                        <Clock className="h-3 w-3 mr-1" />
                        {co.status === "approved" 
                          ? (co.approved_hours || co.time_impact || 0)
                          : (co.time_impact || 0)}h
                      </span>
                      {canViewCosts() && (
                        <span className="flex items-center">
                          <DollarSign className="h-3 w-3 mr-1" />
                          {co.status === "approved"
                            ? (co.approved_cost || co.cost_impact || 0).toLocaleString()
                            : (co.cost_impact || 0).toLocaleString()} {project?.currency || "USD"}
                          {project?.currency === "CAD" && co.usd_cost_impact && (
                            <span className="ml-1 text-gray-400">(≈{co.usd_cost_impact.toLocaleString(undefined, {maximumFractionDigits: 0})} USD)</span>
                          )}
                        </span>
                      )}
                      <span className="flex items-center text-md3-on-surface-variant">
                        {co.created_at && format(new Date(co.created_at), "MMM d, yyyy")}
                      </span>
                    </div>
                  </div>
                );
                })}
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" className="border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full" onClick={() => setVersionHistoryDialogOpen(false)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>


        {/* Invoice Report Dialog */}
        <Dialog open={invoiceReportDialogOpen} onOpenChange={setInvoiceReportDialogOpen}>
          <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto bg-white">
            <DialogHeader>
              <DialogTitle className="text-md3-headline-small text-md3-on-surface font-semibold">Invoice Report</DialogTitle>
              <DialogDescription className="text-md3-body-medium text-gray-600">
                {invoiceReportData?.project?.project_number} — {invoiceReportData?.project?.name}
              </DialogDescription>
            </DialogHeader>

            {invoiceReportData && (
              <div className="space-y-6">
                {/* Project Info */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-blue-50/50 rounded-md3-md border border-blue-200">
                  <div>
                    <p className="text-xs text-md3-on-surface-variant">Project No.</p>
                    <p className="font-semibold text-md3-on-surface" data-testid="invoice-report-project-number">{invoiceReportData.project.project_number || "—"}</p>
                  </div>
                  <div>
                    <p className="text-xs text-md3-on-surface-variant">PO Number</p>
                    <p className="font-semibold text-md3-on-surface">{invoiceReportData.project.purchase_order_number || "—"}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-xs text-md3-on-surface-variant">Client</p>
                    <p className="font-semibold text-md3-on-surface">{invoiceReportData.project.client_name || "—"}</p>
                  </div>
                </div>

                {/* Financial Summary */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-md3-md">
                    <p className="text-xs text-md3-on-surface-variant">Base Contract</p>
                    <p className="text-lg font-bold text-blue-700">${(invoiceReportData.financials.base_contract_value || 0).toLocaleString()}</p>
                  </div>
                  <div className="p-4 bg-purple-50 border border-purple-200 rounded-md3-md">
                    <p className="text-xs text-md3-on-surface-variant">Approved COs</p>
                    <p className="text-lg font-bold text-purple-700">${(invoiceReportData.financials.approved_change_orders || 0).toLocaleString()}</p>
                  </div>
                  <div className="p-4 bg-green-50 border border-green-200 rounded-md3-md">
                    <p className="text-xs text-md3-on-surface-variant">Total Project Value</p>
                    <p className="text-lg font-bold text-green-700">${(invoiceReportData.financials.total_project_value || 0).toLocaleString()}</p>
                  </div>
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-md3-md">
                    <p className="text-xs text-md3-on-surface-variant">Total Invoiced</p>
                    <p className="text-lg font-bold text-amber-700">${(invoiceReportData.financials.total_invoiced || 0).toLocaleString()}</p>
                  </div>
                  <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-md3-md">
                    <p className="text-xs text-md3-on-surface-variant">Total Collected</p>
                    <p className="text-lg font-bold text-emerald-700">${(invoiceReportData.financials.total_collected || 0).toLocaleString()}</p>
                  </div>
                  <div className="p-4 bg-orange-50 border border-orange-200 rounded-md3-md">
                    <p className="text-xs text-md3-on-surface-variant">Balance to Invoice</p>
                    <p className="text-lg font-bold text-orange-700">${(invoiceReportData.financials.balance_to_invoice || 0).toLocaleString()}</p>
                  </div>
                  <div className="p-4 bg-red-50 border border-red-200 rounded-md3-md col-span-2 md:col-span-1">
                    <p className="text-xs text-md3-on-surface-variant">Balance to Collect</p>
                    <p className="text-lg font-bold text-red-700">${(invoiceReportData.financials.balance_to_collect || 0).toLocaleString()}</p>
                  </div>
                </div>

                {/* Invoices Table */}
                <div>
                  <h3 className="text-md3-title-medium font-semibold text-md3-on-surface mb-3">Invoices ({invoiceReportData.invoices?.length || 0})</h3>
                  <div className="overflow-x-auto border rounded-md3-md">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 border-b">
                        <tr>
                          <th className="px-3 py-2 text-left font-semibold text-md3-on-surface">Invoice #</th>
                          <th className="px-3 py-2 text-left font-semibold text-md3-on-surface">Description</th>
                          <th className="px-3 py-2 text-left font-semibold text-md3-on-surface">Issue Date</th>
                          <th className="px-3 py-2 text-left font-semibold text-md3-on-surface">Due Date</th>
                          <th className="px-3 py-2 text-right font-semibold text-md3-on-surface">Amount</th>
                          <th className="px-3 py-2 text-right font-semibold text-md3-on-surface">Paid</th>
                          <th className="px-3 py-2 text-right font-semibold text-md3-on-surface">Balance</th>
                          <th className="px-3 py-2 text-center font-semibold text-md3-on-surface">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(invoiceReportData.invoices || []).length === 0 ? (
                          <tr><td colSpan={8} className="px-3 py-4 text-center text-md3-on-surface-variant">No invoices yet</td></tr>
                        ) : (
                          invoiceReportData.invoices.map((inv, idx) => (
                            <tr key={idx} className="border-b hover:bg-gray-50">
                              <td className="px-3 py-2 font-medium text-blue-700">{inv.invoice_number}</td>
                              <td className="px-3 py-2">{inv.description || "—"}</td>
                              <td className="px-3 py-2">{inv.issue_date ? new Date(inv.issue_date).toLocaleDateString() : "—"}</td>
                              <td className="px-3 py-2">{inv.due_date ? new Date(inv.due_date).toLocaleDateString() : "—"}</td>
                              <td className="px-3 py-2 text-right">${(inv.amount || 0).toLocaleString()}</td>
                              <td className="px-3 py-2 text-right text-green-700">${(inv.amount_paid || 0).toLocaleString()}</td>
                              <td className="px-3 py-2 text-right text-red-700">${(inv.balance_due || 0).toLocaleString()}</td>
                              <td className="px-3 py-2 text-center">
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                  inv.payment_status === 'paid' ? 'bg-green-100 text-green-700' :
                                  inv.payment_status === 'partial' ? 'bg-amber-100 text-amber-700' :
                                  'bg-red-100 text-red-700'
                                }`}>
                                  {inv.payment_status || 'unpaid'}
                                </span>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setInvoiceReportDialogOpen(false)} data-testid="invoice-report-close-btn">Close</Button>
              <Button onClick={handleDownloadInvoiceReport} className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary" data-testid="invoice-report-download-btn">
                <Download className="mr-2 h-4 w-4" /> Download Excel
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteConfirmDialogOpen} onOpenChange={setDeleteConfirmDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Change Order</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete this change order? This action cannot be undone.
                {project && deletingChangeOrderId && (
                  <div className="mt-3 p-3 bg-md3-surface border rounded-md3-md">
                    <p className="text-md3-body-small font-medium text-md3-on-surface">
                      {project.change_orders.find(co => co.id === deletingChangeOrderId)?.change_order_number}
                    </p>
                    <p className="text-xs text-md3-on-surface-variant mt-1">
                      {project.change_orders.find(co => co.id === deletingChangeOrderId)?.description}
                    </p>
                  </div>
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteChangeOrder}
                className="bg-red-600 hover:bg-red-700"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
}